/**
 * RekognitionGateway - Service for AWS Rekognition operations via Parse Cloud
 * 
 * This service provides an abstraction layer for biometric verification operations,
 * calling Parse Cloud functions that interact with AWS Rekognition.
 */

import { Parse } from '../lib/parse';
import { mapRekognitionError } from '../components/verification/verificationUtils';

/**
 * Compresses a base64 image to fit within a maximum byte size.
 * Rekognition APIs have a 5MB limit for image bytes.
 * 
 * @param {string} base64Image - Raw base64 string (no data: prefix)
 * @param {number} maxBytes - Maximum allowed size in bytes (default 4.5MB to leave margin)
 * @returns {Promise<string>} Compressed base64 string
 */
async function compressImageIfNeeded(base64Image, maxBytes = 4500000) {
  if (!base64Image) return base64Image;
  
  // Estimate actual byte size from base64 length
  const estimatedBytes = Math.ceil((base64Image.length * 3) / 4);
  if (estimatedBytes <= maxBytes) {
    return base64Image;
  }

  console.log(`[rekognitionGateway] Image too large (${(estimatedBytes / 1024 / 1024).toFixed(2)}MB), compressing...`);

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      // Calculate scale factor to reduce size
      const scaleFactor = Math.sqrt(maxBytes / estimatedBytes) * 0.85;
      const newWidth = Math.round(img.width * scaleFactor);
      const newHeight = Math.round(img.height * scaleFactor);

      const canvas = document.createElement('canvas');
      canvas.width = newWidth;
      canvas.height = newHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      // Export as JPEG with 0.8 quality
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      const compressed = dataUrl.split(',')[1] || dataUrl;
      console.log(`[rekognitionGateway] Compressed to ${(compressed.length * 3 / 4 / 1024 / 1024).toFixed(2)}MB (${newWidth}x${newHeight})`);
      resolve(compressed);
    };
    img.onerror = () => {
      // If compression fails, return original and let the server handle the error
      resolve(base64Image);
    };
    // Handle both raw base64 and data URL prefixed base64
    const src = base64Image.startsWith('data:')
      ? base64Image
      : `data:image/jpeg;base64,${base64Image}`;
    img.src = src;
  });
}

/**
 * Compares faces between document ID photo and selfie using AWS Rekognition
 * 
 * @param {string} documentImage - Base64 encoded document image
 * @param {string} selfieImage - Base64 encoded selfie image
 * @param {number} threshold - Similarity threshold (0-100)
 * @returns {Promise<Object>} Comparison result with FaceMatches, similarity, liveness scores
 */
async function compareFaces(documentImage, selfieImage, threshold = 85) {
  try {
    const compressedDoc = await compressImageIfNeeded(documentImage);
    const compressedSelfie = await compressImageIfNeeded(selfieImage);
    const result = await Parse.Cloud.run('comparefaces', {
      documentImage: compressedDoc,
      selfieImage: compressedSelfie,
      threshold
    });

    return result;
  } catch (error) {
    console.error('compareFaces error:', error);
    const userMessage = mapRekognitionError(error);
    const enhancedError = new Error(userMessage);
    enhancedError.code = error.code || error.name;
    enhancedError.originalError = error;
    throw enhancedError;
  }
}

/**
 * Validates identity document expiry date using OCR text detection.
 *
 * @param {string} documentFrontImage - Base64 encoded front side image
 * @returns {Promise<{checked: boolean, isExpired: boolean, expiryDate: string|null, reason?: string}>}
 */
async function validateDocumentExpiry(documentFrontImage) {
  const functionNames = ['validateDocumentExpiry', 'validatedocumentexpiry'];
  let lastError = null;

  for (const functionName of functionNames) {
    try {
      const compressedImage = await compressImageIfNeeded(documentFrontImage);
      const result = await Parse.Cloud.run(functionName, {
        documentFrontImage: compressedImage
      });
      return result || { checked: false, isExpired: false, expiryDate: null };
    } catch (error) {
      lastError = error;
      const message = String(error?.message || '').toLowerCase();
      const isMissingFn =
        message.includes('invalid function') || message.includes('function not found');
      if (isMissingFn) {
        continue;
      }
      console.error('validateDocumentExpiry error:', error);
      const userMessage =
        error?.message || 'Failed to validate document expiry date. Please try again.';
      const enhancedError = new Error(userMessage);
      enhancedError.code = error.code || error.name;
      enhancedError.originalError = error;
      throw enhancedError;
    }
  }

  const userMessage =
    lastError?.message ||
    'Identity document expiry validation is not available in this server instance.';
  const enhancedError = new Error(userMessage);
  enhancedError.code = lastError?.code || lastError?.name;
  enhancedError.originalError = lastError;
  throw enhancedError;
}

/**
 * Saves verification evidence to Parse Server
 * 
 * @param {Object} evidence - Verification evidence object
 * @param {string} evidence.docId - Document ID
 * @param {string} evidence.signerObjId - Signer object ID
 * @param {string} evidence.provider - Provider name (e.g., 'rekognition')
 * @param {number} evidence.similarity - Similarity score
 * @param {Object} evidence.matchedPerson - Matched person metadata
 * @param {number} evidence.livenessScore - Liveness score
 * @param {string} evidence.livenessConfidence - Liveness confidence (HIGH/MEDIUM/LOW)
 * @param {string} evidence.notes - Additional notes
 * @param {Object} evidence.evidence - Evidence object with images
 * @param {number} evidence.verifiedAt - Verification timestamp
 * @returns {Promise<Object>} Save result
 */
async function saveVerification(evidence) {
  try {
    const result = await Parse.Cloud.run('saveidentityverification', {
      docId: evidence.docId,
      signerObjId: evidence.signerObjId,
      provider: evidence.provider || 'rekognition',
      similarity: evidence.similarity,
      matchedPerson: evidence.matchedPerson,
      livenessScore: evidence.livenessScore,
      livenessConfidence: evidence.livenessConfidence,
      notes: evidence.notes || '',
      evidence: evidence.evidence,
      verifiedAt: evidence.verifiedAt || Date.now()
    });

    return { success: true, result };
  } catch (error) {
    console.error('saveVerification error:', error);
    const userMessage = error.message || 'Failed to save verification evidence';
    const enhancedError = new Error(userMessage);
    enhancedError.code = error.code || error.name;
    enhancedError.originalError = error;
    throw enhancedError;
  }
}

/**
 * RekognitionGateway object with all service methods
 */
const rekognitionGateway = {
  compareFaces,
  validateDocumentExpiry,
  saveVerification
};

export default rekognitionGateway;
